/**
 * Reads/writes OSP credentials in the same localStorage shape as SDK CredentialsStorage.
 *
 * @source @open-social-protocol/osp-api-index-types/src/storage/credentials-storage.ts
 */

import { OspCredentials } from "./credentials";

const STORAGE_VERSION = 1;

interface StorageItem<T> {
  data: T;
  metadata: {
    version: number;
    createdAt: number;
    updatedAt: number;
  };
}

interface AuthStorageData {
  accessToken?: string;
  refreshToken?: string;
}

interface IdTokenStorageData {
  idToken: string;
}

export interface OspStorageProvider {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

class InMemoryOspStorageProvider implements OspStorageProvider {
  private readonly store = new Map<string, string>();

  getItem(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value);
  }

  removeItem(key: string): void {
    this.store.delete(key);
  }
}

function credentialsStorageKey(indexBaseUrl: string): string {
  return `osp.${indexBaseUrl}.credentials`;
}

function idTokenStorageKey(indexBaseUrl: string): string {
  return `osp.${indexBaseUrl}.idToken`;
}

export class OspCredentialsStorage {
  private readonly credentialsKey: string;

  private readonly idTokenKey: string;

  constructor(
    private readonly provider: OspStorageProvider,
    indexBaseUrl: string,
  ) {
    this.credentialsKey = credentialsStorageKey(indexBaseUrl);
    this.idTokenKey = idTokenStorageKey(indexBaseUrl);
  }

  async getCredentials(): Promise<OspCredentials | null> {
    const raw = await this.provider.getItem(this.credentialsKey);
    if (!raw) {
      return null;
    }
    try {
      const item = JSON.parse(raw) as StorageItem<AuthStorageData>;
      const data = item?.data;
      if (!data) {
        return null;
      }
      return new OspCredentials(data.accessToken, data.refreshToken);
    } catch {
      return null;
    }
  }

  async setCredentials(credentials: OspCredentials): Promise<void> {
    const existing = await this.readStorageItem(this.credentialsKey);
    const now = Date.now();
    const item: StorageItem<AuthStorageData> = {
      data: {
        accessToken: credentials.accessToken,
        refreshToken: credentials.refreshToken,
      },
      metadata: {
        version: STORAGE_VERSION,
        createdAt: existing?.metadata.createdAt ?? now,
        updatedAt: now,
      },
    };
    await this.provider.setItem(this.credentialsKey, JSON.stringify(item));
  }

  async setIdToken(idToken: string): Promise<void> {
    const existing = await this.readIdTokenItem();
    const now = Date.now();
    const item: StorageItem<IdTokenStorageData> = {
      data: { idToken },
      metadata: {
        version: STORAGE_VERSION,
        createdAt: existing?.metadata.createdAt ?? now,
        updatedAt: now,
      },
    };
    await this.provider.setItem(this.idTokenKey, JSON.stringify(item));
  }

  async getIdToken(): Promise<string | null> {
    const item = await this.readIdTokenItem();
    return item?.data.idToken ?? null;
  }

  async reset(): Promise<void> {
    await this.provider.removeItem(this.credentialsKey);
    await this.provider.removeItem(this.idTokenKey);
  }

  private async readIdTokenItem(): Promise<StorageItem<IdTokenStorageData> | null> {
    const raw = await this.provider.getItem(this.idTokenKey);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as StorageItem<IdTokenStorageData>;
    } catch {
      return null;
    }
  }

  private async readStorageItem(
    key: string,
  ): Promise<StorageItem<AuthStorageData> | null> {
    const raw = await this.provider.getItem(key);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as StorageItem<AuthStorageData>;
    } catch {
      return null;
    }
  }
}

export function getBrowserOspStorageProvider(): OspStorageProvider {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return new InMemoryOspStorageProvider();
}
